import { useState } from 'react';

import { TextField } from '@components/text-field';
import { useScopedTranslate } from '@locale/translate';
import { validateEmail } from '@utils/validators';

import { EditableCellPopover, FieldEditorBody } from './editable-cell-popover';
import { editableCellLocale } from './locale';
import { EditableCellEditorProps } from './types';
import { useEditableCellTrigger } from './use-editable-cell-trigger';

import { typeRenderMap } from '../renderers';

type EditableEmailCellProps = EditableCellEditorProps<string | undefined>;

export const EditableEmailCell = ({ column, row, value }: EditableEmailCellProps) => {
  const { t } = useScopedTranslate(editableCellLocale);
  const getTrigger = useEditableCellTrigger();
  const [draft, setDraft] = useState(value ?? '');

  const trimmedDraft = draft.trim();
  const validationError =
    trimmedDraft !== '' && !validateEmail(trimmedDraft) ? t('invalidEmail') : undefined;

  const trigger = getTrigger(value, column, row, () => typeRenderMap.email(value, column, row));

  return (
    <EditableCellPopover
      trigger={trigger}
      column={column}
      row={row}
      value={value}
      validationError={validationError}
      isValidationErrorInField
      onOpen={() => setDraft(value ?? '')}
      isSaveDisabled={trimmedDraft === (value ?? '')}
      onSave={async () => {
        await column.onEdit?.(row, trimmedDraft);
      }}
    >
      <FieldEditorBody>
        <TextField
          label={column.title}
          value={draft}
          onChange={setDraft}
          error={validationError}
          autoFocus
        />
      </FieldEditorBody>
    </EditableCellPopover>
  );
};
